import { useCallback } from 'react';

interface ShareParams {
  url: string;
  code: string;
  username: string;
}

interface ShareOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useShare = () => {
  // 生成分享内容
  const generateShareContent = useCallback(({ url, code, username }: ShareParams) => {
    return `Exploring Billion Live — a new Web3 LiveFi platform.

Watch • Trade • Play • Earn  
Join with my invite 👇  
${url}
${code ? `Code：${code}` : ''}`;
  }, []);

  // 分享到 X (Twitter)
  const shareToX = useCallback((params: ShareParams, options?: ShareOptions) => {
    try {
      const content = generateShareContent(params);
      const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(content)}`;
      window.open(twitterUrl, '_blank', 'width=600,height=400');
      options?.onSuccess?.();
    } catch (error) {
      options?.onError?.(error as Error);
    }
  }, [generateShareContent]);

  // 分享到 Discord
  const shareToDiscord = useCallback((params: ShareParams, options?: ShareOptions) => {
    try {
      const content = generateShareContent(params);
      const discordUrl = `https://discord.com/channels/@me`;
      // Discord 没有直接的分享API，所以打开Discord应用
      window.open(discordUrl, '_blank');
      
      // 复制内容到剪贴板，用户可以在Discord中粘贴
      navigator.clipboard.writeText(content).then(() => {
        options?.onSuccess?.();
      }).catch((error) => {
        options?.onError?.(error);
      });
    } catch (error) {
      options?.onError?.(error as Error);
    }
  }, [generateShareContent]);

  // 分享到 Telegram
  const shareToTelegram = useCallback((params: ShareParams, options?: ShareOptions) => {
    try {
      const content = generateShareContent(params);
      const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(params.url)}&text=${encodeURIComponent(content)}`;
      window.open(telegramUrl, '_blank', 'width=600,height=400');
      options?.onSuccess?.();
    } catch (error) {
      options?.onError?.(error as Error);
    }
  }, [generateShareContent]);

  // 分享到 Line
  const shareToLine = useCallback((params: ShareParams, options?: ShareOptions) => {
    try {
      const content = generateShareContent(params);
      const lineUrl = `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(params.url)}&text=${encodeURIComponent(content)}`;
      window.open(lineUrl, '_blank', 'width=600,height=400');
      options?.onSuccess?.();
    } catch (error) {
      options?.onError?.(error as Error);
    }
  }, [generateShareContent]);

  // 复制分享内容到剪贴板
  const copyShareContent = useCallback(async (params: ShareParams, options?: ShareOptions) => {
    const content = generateShareContent(params);
    try {
      await navigator.clipboard.writeText(content);
      options?.onSuccess?.();
    } catch (error) {
      // 降级方案
      try {
        const textArea = document.createElement('textarea');
        textArea.value = content;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        options?.onSuccess?.();
      } catch (fallbackError) {
        options?.onError?.(fallbackError as Error);
      }
    }
  }, [generateShareContent]);

  // 复制代码到剪贴板
  const copyCode = useCallback(async (code: string, options?: ShareOptions) => {
    try {
      await navigator.clipboard.writeText(code);
      options?.onSuccess?.();
    } catch (error) {
      // 降级方案
      try {
        const textArea = document.createElement('textarea');
        textArea.value = code;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        options?.onSuccess?.();
      } catch (fallbackError) {
        options?.onError?.(fallbackError as Error);
      }
    }
  }, []);

  // 通用分享函数
  const share = useCallback((platform: 'x' | 'discord' | 'telegram' | 'line' | 'copy', params: ShareParams, options?: ShareOptions) => {
    switch (platform) {
      case 'x':
        shareToX(params, options);
        break;
      case 'discord':
        shareToDiscord(params, options);
        break;
      case 'telegram':
        shareToTelegram(params, options);
        break;
      case 'line':
        shareToLine(params, options);
        break;
      case 'copy':
        copyShareContent(params, options);
        break;
      default:
        options?.onError?.(new Error(`Unsupported platform: ${platform}`));
    }
  }, [shareToX, shareToDiscord, shareToTelegram, shareToLine, copyShareContent]);

  return {
    share,
    shareToX,
    shareToDiscord,
    shareToTelegram,
    shareToLine,
    copyShareContent,
    copyCode,
    generateShareContent,
  };
};
